import { LayoutFieldType } from "@whatslly/types/LayoutFieldType";
import { LayoutFieldsSetupType } from "@whatslly/types/LayoutFieldsSetupType";
import { PersonType } from "@whatslly/types/Person";
import { MatchedByType } from "@whatslly/types/QueryResponse";
import { SelectedContactType } from "@whatslly/types/SelectedContactType";
import { OppType } from "@whatslly/types/SfQueryApiResponse";
import { ThreadMessagesResponseType } from "@whatslly/types/ThreadMessagesResponse";
import { UserType } from "@whatslly/types/User";
import { WhatsllyGroupType } from "@whatslly/types/WhatsllyGroup";
import type { ToastProps } from "@whatslly/ui-components/v2/Toaster/Toast";
import type { SelectedChatMessagesType } from "@whatslly/types/SelectedChatMessageType";
import { IMChannelType } from "@whatslly/instant-messaging/channelTypes";
import type { WsOutgoingMessage } from "@whatslly/types/tuvis-agent/messages";

export const WEB_WIDGET_COMMANDS_PREFIX = "whatslly-widget";

export const WidgetCommands = {
  SAVE_WAP_VERSION: `${WEB_WIDGET_COMMANDS_PREFIX}:save-wap-version`,
  ABORT_TRANSACTION: `${WEB_WIDGET_COMMANDS_PREFIX}:abort-transaction`,
  RESPONSE: `${WEB_WIDGET_COMMANDS_PREFIX}:response`,
  ERROR: `${WEB_WIDGET_COMMANDS_PREFIX}:error`,
  SHOW_LOADING: `${WEB_WIDGET_COMMANDS_PREFIX}:show-loading`,
  HIDE_LOADING: `${WEB_WIDGET_COMMANDS_PREFIX}:hide-loading`,
  UPDATE_VERSION: `${WEB_WIDGET_COMMANDS_PREFIX}:update-version`,
  USER_WITHOUT_PERMISSION: `${WEB_WIDGET_COMMANDS_PREFIX}:user-without-permission`,
  USER_MISSING_PERMISSION: `${WEB_WIDGET_COMMANDS_PREFIX}:user-missing-permission`,
  QUERY_FAILED: `${WEB_WIDGET_COMMANDS_PREFIX}:query-failed`,
  GROUPS_NOT_SUPPORTED: `${WEB_WIDGET_COMMANDS_PREFIX}:groups-not-supported`,
  EXTENSION_NOT_SUPPORTED: `${WEB_WIDGET_COMMANDS_PREFIX}:extension-not-supported`,
  CONTACT_DESELECTED: `${WEB_WIDGET_COMMANDS_PREFIX}:contact-deselected`,
  LOGIN_AGAIN: `${WEB_WIDGET_COMMANDS_PREFIX}:login-again`,
  SHOW_PERSON_PAGE: `${WEB_WIDGET_COMMANDS_PREFIX}:show-person-page`,
  SHOW_GROUP_PAGE: `${WEB_WIDGET_COMMANDS_PREFIX}:show-group-page`,
  SAVE_USER_PHONE: `${WEB_WIDGET_COMMANDS_PREFIX}:save-user-phone`,
  THEME_MODE_CHANGED: `${WEB_WIDGET_COMMANDS_PREFIX}:theme-mode-changed`,
  WINDOW_RESIZE: `${WEB_WIDGET_COMMANDS_PREFIX}:window-resize`,
  UPDATE_AVAILABLE: `${WEB_WIDGET_COMMANDS_PREFIX}:update-available`,
  MESSAGES_SELECTED: `${WEB_WIDGET_COMMANDS_PREFIX}:messages-selected`,
  LOGOUT: `${WEB_WIDGET_COMMANDS_PREFIX}:logout`,
  OPEN_CREATE_RECORD_PAGE: `${WEB_WIDGET_COMMANDS_PREFIX}:open-create-record-page`,
  OPEN_CHAT_NOW_MODAL: `${WEB_WIDGET_COMMANDS_PREFIX}:open-chat-now-modal`,
  STATUS_BAR_ITEM_SELECTED: `${WEB_WIDGET_COMMANDS_PREFIX}:status-bar-item-selected`,
  MESSAGES_SYNCED: `${WEB_WIDGET_COMMANDS_PREFIX}:messages-synced`,
  INSTANT_MESSAGING_TAB_CHANGED: `${WEB_WIDGET_COMMANDS_PREFIX}:instant-messaging-tab-changed`,
  INSTANT_MESSAGING_TAB_OPEN: `${WEB_WIDGET_COMMANDS_PREFIX}:instant-messaging-tab-open`,
  INSTANT_MESSAGING_OPEN_ADD_CHANNEL: `${WEB_WIDGET_COMMANDS_PREFIX}:instant-messaging-open-add-channel`,
  INSTANT_MESSAGE_INIT: `${WEB_WIDGET_COMMANDS_PREFIX}:instant-message-init`,
  SYNC_WHATSAPP_BLUR_MODE: `${WEB_WIDGET_COMMANDS_PREFIX}:sync-whatsapp-blur-mode`,
  SHOW_FILTERS_BUTTON_AND_SEARCH: `${WEB_WIDGET_COMMANDS_PREFIX}:show-filters-button-and-search`,
  OPEN_TOAST: `${WEB_WIDGET_COMMANDS_PREFIX}:show-toast`,
  OPEN_SHOW_FRAUD_DETECTED_MODAL: `${WEB_WIDGET_COMMANDS_PREFIX}:open-show-fraud-detected-modal`,
  INSTANT_MESSAGING_LABELS_UPDATED: `${WEB_WIDGET_COMMANDS_PREFIX}:instant-messaging-labels-updated`,
  STOP_COPY_MONITOR: `${WEB_WIDGET_COMMANDS_PREFIX}:stop-copy-monitor`,
  ON_COPY: `${WEB_WIDGET_COMMANDS_PREFIX}:on-copy`,
  AI_CHAT_EVENT: `${WEB_WIDGET_COMMANDS_PREFIX}:ai-chat-event`,
} as const;

export type WidgetCommandsKeysType = keyof typeof WidgetCommands;

export type WidgetCommandTypes = {
  [WidgetCommands.ABORT_TRANSACTION]: never;
  [WidgetCommands.RESPONSE]: never;
  [WidgetCommands.ERROR]: never;
  [WidgetCommands.SHOW_LOADING]: () => void;
  [WidgetCommands.HIDE_LOADING]: () => void;
  [WidgetCommands.UPDATE_VERSION]: () => void;
  [WidgetCommands.SAVE_WAP_VERSION]: ({ wapVersion }: { wapVersion: string }) => void;
  [WidgetCommands.USER_WITHOUT_PERMISSION]: () => void;
  [WidgetCommands.USER_MISSING_PERMISSION]: () => void;
  [WidgetCommands.QUERY_FAILED]: (data: { error: string } | undefined) => void;
  [WidgetCommands.GROUPS_NOT_SUPPORTED]: (data: { user?: UserType }) => void;
  [WidgetCommands.EXTENSION_NOT_SUPPORTED]: (data: { user?: UserType; selectedContact?: SelectedContactType }) => void;
  [WidgetCommands.CONTACT_DESELECTED]: () => void;
  [WidgetCommands.LOGIN_AGAIN]: () => void;
  [WidgetCommands.SHOW_PERSON_PAGE]: (data: {
    person: PersonType | PersonType[];
    user?: UserType;
    matchedBy?: MatchedByType;
    foundMultiple?: boolean;
    selectedContact?: SelectedContactType;
    msgs?: ThreadMessagesResponseType;
    whatsllyPersonId?: string;
    fields?: LayoutFieldsSetupType;
    opps?: OppType[];
    openOpps?: OppType[];
    originalRecordAmount?: number;
    etoken?: string;
    foundByDefault?: boolean;
    messagesAreBlocked?: boolean;
  }) => void;
  [WidgetCommands.SHOW_GROUP_PAGE]: (data: {
    selectedContact?: SelectedContactType;
    group?: WhatsllyGroupType;
    user?: UserType;
    msgs?: ThreadMessagesResponseType;
    fields?: LayoutFieldsSetupType;
    etoken?: string;
    messagesAreBlocked?: boolean;
  }) => void;
  [WidgetCommands.SAVE_USER_PHONE]: (data: { phone?: string }) => void;
  [WidgetCommands.THEME_MODE_CHANGED]: (data: { darkMode: boolean }) => void;
  [WidgetCommands.WINDOW_RESIZE]: never;
  [WidgetCommands.UPDATE_AVAILABLE]: (data: { platform: "desktop" | "browserExt" | ""; message: any }) => void;
  [WidgetCommands.MESSAGES_SELECTED]: (data: SelectedChatMessagesType) => void;
  [WidgetCommands.LOGOUT]: never;
  [WidgetCommands.OPEN_CREATE_RECORD_PAGE]: (data: { type: "contact" | "lead" | "person" }) => void;
  [WidgetCommands.OPEN_CHAT_NOW_MODAL]: (data: { phone: string; countryCode: string | undefined }) => void;
  [WidgetCommands.STATUS_BAR_ITEM_SELECTED]: (data: {
    parentItemId: string;
    fields?: LayoutFieldType[];
    action: "modal" | "set";
    selectedItemApiName: string;
    prevSelectedItemValue: string;
    selectedItemValue: string;
    parentItemType: "Opportunity" | "Lead";
  }) => void;
  [WidgetCommands.MESSAGES_SYNCED]: (data: { success: boolean }) => void;
  [WidgetCommands.INSTANT_MESSAGING_TAB_CHANGED]: (data: { channelId: string; channelName: string }) => void;
  [WidgetCommands.INSTANT_MESSAGING_TAB_OPEN]: (data: { channelId: string; channelName?: string }) => void;
  [WidgetCommands.INSTANT_MESSAGING_OPEN_ADD_CHANNEL]: (data: { channelName?: string }) => void;
  [WidgetCommands.INSTANT_MESSAGE_INIT]: (data: { channelName: string }) => void;
  [WidgetCommands.SYNC_WHATSAPP_BLUR_MODE]: (data?: { channelName?: IMChannelType }) => void;
  [WidgetCommands.SHOW_FILTERS_BUTTON_AND_SEARCH]: (data: { show: boolean }) => void;
  [WidgetCommands.OPEN_TOAST]: (data: ToastProps) => void;
  [WidgetCommands.OPEN_SHOW_FRAUD_DETECTED_MODAL]: (messageId: string) => Promise<boolean>;
  [WidgetCommands.INSTANT_MESSAGING_LABELS_UPDATED]: (labels: Record<string, string>) => void;
  [WidgetCommands.STOP_COPY_MONITOR]: () => void;
  [WidgetCommands.ON_COPY]: (text: string) => void;
  [WidgetCommands.AI_CHAT_EVENT]: (data: WsOutgoingMessage) => void;
};
